import { produce } from 'immer';
import {
  IOperationOutcome,
  isOperationOutcome,
} from '../modules/unsw-fhir/OperationOutcome';
import { FhirInstance } from '../config/FhirClient';
import { IBundle } from '../modules/unsw-fhir/Bundle';
import { ICondition, isCondition } from '../modules/unsw-fhir/Condition';
import { isCodeableConceptEqual } from '../modules/unsw-fhir/CodeableConcept';

const getConditionsByPatientId = async (
  patientId: string,
): Promise<ICondition[]> => {
  try {
    const response = await FhirInstance.get<IBundle | IOperationOutcome>(
      '/Condition',
      { params: { subject: patientId } },
    );
    const { data } = response;
    if (isOperationOutcome(data) || !data.entry) {
      return [];
    }
    return data.entry
      .filter(entry => {
        return !!entry.resource && isCondition(entry.resource);
      })
      .map((entry): ICondition => entry.resource as ICondition);
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const updateConditionsByPatientId = async (
  conditions: ICondition[],
  patientId: string,
) => {
  const savedConditions = await getConditionsByPatientId(patientId);
  const newConditions = conditions.filter(
    condition =>
      !savedConditions.some(savedCondition =>
        isCodeableConceptEqual(condition.code, savedCondition.code),
      ),
  );
  const removedConditions = savedConditions
    .filter(
      savedCondition =>
        !conditions.some(condition =>
          isCodeableConceptEqual(condition.code, savedCondition.code),
        ),
    )
    .map(savedConditions =>
      produce(savedConditions, draft => {
        draft.clinicalStatus = 'inactive';
      }),
    );

  const modifiedConditions = savedConditions
    .filter(savedCondition =>
      conditions.some(
        condition =>
          isCodeableConceptEqual(condition.code, savedCondition.code) &&
          condition.clinicalStatus === 'inactive',
      ),
    )
    .map(savedConditions =>
      produce(savedConditions, draft => {
        draft.clinicalStatus = 'active';
      }),
    );
  const modifiedConditions_tsk = Promise.all(
    [...removedConditions, ...modifiedConditions].map(async condition =>
      putCondition(condition.id!, condition),
    ),
  );
  const postedConditions_tsk = Promise.all(
    newConditions.map(async condition => postCondition(condition)),
  );

  await Promise.all([modifiedConditions_tsk, postedConditions_tsk]);
  return await getConditionsByPatientId(patientId);
};

export const postCondition = async (condition: ICondition) => {
  try {
    const response = await FhirInstance.post<ICondition | IOperationOutcome>(
      '/Condition',
      condition,
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const putCondition = async (id: string, condition: ICondition) => {
  try {
    const response = await FhirInstance.put<ICondition | IOperationOutcome>(
      `/Condition/${id}`,
      condition,
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const deleteConditionById = async (id: string) => {
  try {
    const response = await FhirInstance.delete<ICondition | IOperationOutcome>(
      `/Condition/${id}`,
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const getConditions = async () => {
  try {
    const response = await FhirInstance.get<IBundle | IOperationOutcome>(
      '/Condition',
    );
    const { data } = response;
    if (isOperationOutcome(data) || !data.entry) {
      return [];
    }

    return data.entry
      .filter(entry => {
        return !!entry.resource && isCondition(entry.resource);
      })
      .map((entry): ICondition => entry.resource as ICondition);
  } catch (e) {
    console.error(e);
    return [];
  }
};
